import objFactory from "_src/engine/factories/object";
import primitiveFactory from "_src/engine/factories/primitive";
import FrameBuffer from "_src/engine/framebuffer";
import {
  primitiveType,
  blendFactor,
  blendEquation,
  pixelBufferType,
  RBIntFormat,
  attachmentId
} from "_src/engine/constants";

import Pass from "_src/render_graph/pass";
import resourceManager from "_src/render_graph/resource_manager";
import SelectionInterfaceShader from "_src/shaders/selection/selection_interface";
import SelectionMaskShader from "_src/shaders/selection/selection_mask";
import SmartBlending from "_src/shaders/selection/blending";
import CopyShader from "_src/shaders/copy/copy";
import config from "_src/config";

const useSmartBlending = false;

export default class SelectionPass extends Pass {

  constructor(attachmentsStorage, camera) {

    super(attachmentsStorage);

    this.init(camera);

  }

  init(camera) {

    const maskScene = this._addScene(camera);
    if (useSmartBlending) {
      this._addScene(camera);
    }
    const copyScene = this._addScene(camera);
    const interfaceScene = this._addScene(camera);

    interfaceScene.clearedColor = false;
    interfaceScene.clearedStencil = false;

    copyScene.clearedColor = false;
    copyScene.clearedStencil = false;

    const interfaceShader = new SelectionInterfaceShader({ transparent : true });

    const maskShader = new SelectionMaskShader({ transparent : true });
    maskShader.setSourceBlendFactor(blendFactor.ONE);
    maskShader.setBlendEquation(blendEquation.MAX, blendEquation.MAX);

    const copyShader = new CopyShader({transparent : true});

    const interfacePlane = objFactory.createDrawable(interfaceShader);
    interfacePlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));

    const maskPlane = objFactory.createDrawable(maskShader);
    maskPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    maskPlane.setOpacity(0.3);

    const copyScreenPlane = objFactory.createDrawable(copyShader);
    copyScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));

    const sc = config.selectionColor;
    maskPlane.setDiffuseColor(sc.r, sc.g, sc.b);
    interfacePlane.setDiffuseColor(0.0, 0.0, 0.0);

    interfaceScene.add(interfacePlane);
    maskScene.add(maskPlane);
    copyScene.add(copyScreenPlane);

    if (useSmartBlending) {
      const scenes = this.getScenes();
      const blendScene = scenes[1];
      const blendShader = new SmartBlending();
      const blendScreenPlane = objFactory.createDrawable(blendShader);
      blendScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
      blendScene.add(blendScreenPlane);
      maskPlane.setOpacity(1.0);
    }

  }

  update() {

    const scenes = this.getScenes();
    const maskScene = scenes[0];
    const copyScene = scenes[useSmartBlending ? 2 : 1];
    const interfaceScene = scenes[useSmartBlending ? 3 : 2];

    const width = maskScene.camera.viewport[2];
    const height = maskScene.camera.viewport[3];

    const { drawFrameBuffer, blendFrameBuffer, copyFrameBuffer } = this.initFramebuffer(width, height);

    maskScene.setFramebuffer(drawFrameBuffer);
    copyScene.setFramebuffer(copyFrameBuffer);
    interfaceScene.setFramebuffer(copyFrameBuffer);

    if (useSmartBlending) {
      const blendScene = scenes[1];
      blendScene.setFramebuffer(blendFrameBuffer);
    }

  }

  initFramebuffer(width, height) {

    const colorTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA,
        { width, height });
    const format = RBIntFormat.D16U;
    const depthBuffer = resourceManager.initBuffer(pixelBufferType.RB_DEPTH,
        { width, height, format });

    const scenes = this.getScenes();
    const copyScene = scenes[useSmartBlending ? 2 : 1];

    const copyScreenPlane = this.getScreenQuad(copyScene);
    copyScreenPlane.setTexture(colorTexture, 0);

    let blendFrameBuffer = null;
    if (useSmartBlending) {
      const blendScene = scenes[1];
      const blendScreenPlane = this.getScreenQuad(blendScene);

      blendScreenPlane.setTexture(colorTexture, 0);
      blendScreenPlane.setTexture(this.attachmentsStorage[attachmentId.OUTPUT_COLOR_TEXTURE], 1);

      const copyTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA, { width, height });
      copyScreenPlane.setTexture(copyTexture, 0);
      blendFrameBuffer = new FrameBuffer([copyTexture], depthBuffer);
    }

    const drawFrameBuffer = new FrameBuffer([colorTexture], depthBuffer);
    const copyFrameBuffer = new FrameBuffer([this.attachmentsStorage[attachmentId.OUTPUT_COLOR_TEXTURE]],
      this.attachmentsStorage[attachmentId.INTERFACE_DEPTH]);

    return { drawFrameBuffer, blendFrameBuffer, copyFrameBuffer };

  }

}
